import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useWhatsApp } from '../context/WhatsAppContext';
import api from './api';

const PROVIDERS = [
  {
    id: 'manual',
    label: '📱 Manual (wa.me)',
    color: '#25D366',
    desc: 'Opens WhatsApp with pre-filled message. User presses Send manually. No API needed.',
    fields: [],
  },
  {
    id: 'wati',
    label: '⚡ WATI',
    color: '#4f46e5',
    desc: 'Sends via WATI API automatically. Requires a WATI account and active WhatsApp Business number.',
    fields: [
      { key: 'watiUrl', label: 'WATI API Base URL', placeholder: 'https://live-server.wati.io', type: 'url' },
      { key: 'watiToken', label: 'WATI Bearer Token', placeholder: 'eyJhbGci…', type: 'password' },
    ],
  },
  {
    id: 'twilio',
    label: '📡 Twilio',
    color: '#ef4444',
    desc: 'Sends via Twilio WhatsApp API automatically. Requires Twilio account + WhatsApp sandbox or approved number.',
    fields: [
      { key: 'twilioSid', label: 'Twilio Account SID', placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx', type: 'text' },
      { key: 'twilioToken', label: 'Twilio Auth Token', placeholder: 'your auth token', type: 'password' },
      { key: 'twilioFrom', label: 'Twilio WhatsApp Number', placeholder: '14155238886 (digits only)', type: 'tel' },
    ],
  },
  {
    id: 'aisensy',
    label: '🟢 AiSensy',
    color: '#00a550',
    desc: 'Sends via AiSensy Direct API. Requires AiSensy account with an active WhatsApp Business number and a template campaign.',
    fields: [
      { key: 'aisensyApiKey', label: 'AiSensy API Key', placeholder: 'your-aisensy-api-key', type: 'password' },
      { key: 'aisensyUserName', label: 'AiSensy Account User Name', placeholder: 'MYC CRM', type: 'text' },
      { key: 'aisensyCampaignName', label: 'Campaign / Template Name', placeholder: 'myc_whatsapp_message', type: 'text' },
    ],
  },
  {
    id: 'meta',
    label: '🔵 Meta Cloud API',
    color: '#1877F2',
    desc: 'Sends directly via Meta WhatsApp Cloud API. No broker (WATI/AiSensy) needed. Requires a Meta Business app with WhatsApp product enabled.',
    fields: [
      { key: 'metaPhoneNumberId', label: 'WhatsApp Phone Number ID', placeholder: '106XXXXXXXXXXXX', type: 'text' },
      { key: 'metaAccessToken', label: 'Access Token', placeholder: 'EAAxxxxxxx… (from API Setup)', type: 'password' },
      { key: 'metaAppSecret', label: 'App Secret', placeholder: 'from Settings → Basic → App Secret', type: 'password' },
      { key: 'metaTemplateName', label: 'Template Name (blank = plain text)', placeholder: 'leave blank for free text messages', type: 'text' },
      { key: 'metaTemplateLang', label: 'Template Language', placeholder: 'en_US', type: 'text' },
    ],
  },
];

export default function WhatsAppConfigModal() {
  const { config, saveConfig, setConfigOpen } = useWhatsApp();
  const [draft, setDraft] = useState({ ...config });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const provider = PROVIDERS.find(p => p.id === draft.provider) || PROVIDERS[0];
  const accentColor = provider.color;

  const setField = (key, val) => setDraft(d => ({ ...d, [key]: val }));

  const handleSave = () => {
    setSaving(true);
    saveConfig(draft);
    setTimeout(() => { setSaving(false); setConfigOpen(false); }, 300);
  };

  const handleTest = async () => {
    if (!testPhone.trim()) { alert('Enter a phone number to test with.'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const clean = testPhone.replace(/\D/g, '');
      const e164 = clean.startsWith('91') ? clean : `91${clean}`;
      if (draft.provider === 'manual') {
        window.open(`https://wa.me/${e164}?text=${encodeURIComponent('Test message from MYC WhatsApp config ✅')}`, '_blank');
        setTestResult({ ok: true, msg: 'Opened WhatsApp link — manual mode works.' });
      } else {
        await api.post(`${process.env.REACT_APP_URL}/whatsapp/send`, {
          to: e164,
          message: 'Test message from MYC WhatsApp config ✅',
          provider: draft.provider,
          watiUrl: draft.watiUrl,
          watiToken: draft.watiToken,
          twilioSid: draft.twilioSid,
          twilioToken: draft.twilioToken,
          twilioFrom: draft.twilioFrom,
          aisensyApiKey: draft.aisensyApiKey,
          aisensyUserName: draft.aisensyUserName,
          aisensyCampaignName: draft.aisensyCampaignName,
          metaPhoneNumberId: draft.metaPhoneNumberId,
          metaAccessToken: draft.metaAccessToken,
          metaAppSecret: draft.metaAppSecret,
          metaTemplateName: draft.metaTemplateName,
          metaTemplateLang: draft.metaTemplateLang,
        });
        setTestResult({ ok: true, msg: `✅ Message sent via ${draft.provider.toUpperCase()} successfully!` });
      }
    } catch (err) {
      setTestResult({ ok: false, msg: `❌ Failed: ${err?.response?.data?.error || err.message}` });
    } finally {
      setTesting(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999999 }}
      onClick={() => setConfigOpen(false)}
    >
      <div
        style={{ background: '#fff', borderRadius: 16, width: 560, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 32px 80px rgba(0,0,0,0.35)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${accentColor}, #0d9488)`, color: '#fff', padding: '18px 22px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17 }}>📲 WhatsApp Configuration</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>App-wide WhatsApp provider for all messages</div>
          </div>
          <button onClick={() => setConfigOpen(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '20px 22px' }}>

          {/* Provider selector */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Select Provider
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PROVIDERS.map(p => (
                <div
                  key={p.id}
                  onClick={() => setField('provider', p.id)}
                  style={{
                    padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                    border: `2px solid ${draft.provider === p.id ? p.color : '#e5e7eb'}`,
                    background: draft.provider === p.id ? `${p.color}10` : '#fafafa',
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', border: `2px solid ${p.color}`,
                    background: draft.provider === p.id ? p.color : 'transparent',
                    flexShrink: 0, marginTop: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {draft.provider === p.id && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: draft.provider === p.id ? p.color : '#1f2937' }}>{p.label}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.4 }}>{p.desc}</div>
                  </div>
                  {draft.provider === p.id && (
                    <span style={{ marginLeft: 'auto', fontSize: 11, background: p.color, color: '#fff', borderRadius: 8, padding: '2px 8px', fontWeight: 700, flexShrink: 0 }}>
                      Active
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Provider-specific fields */}
          {provider.fields.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {provider.label} Credentials
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {provider.fields.map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                      {f.label}
                    </label>
                    <input
                      type={f.type}
                      value={draft[f.key] || ''}
                      onChange={e => setField(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      style={{
                        width: '100%', padding: '8px 11px', borderRadius: 8,
                        border: `1.5px solid ${draft[f.key] ? accentColor : '#d1d5db'}`,
                        fontSize: 13, boxSizing: 'border-box', outline: 'none',
                        fontFamily: f.type === 'password' ? 'monospace' : 'inherit',
                      }}
                    />
                  </div>
                ))}
              </div>
              {/* Help links */}
              {draft.provider === 'wati' && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, fontSize: 11, color: '#1d4ed8' }}>
                  💡 Find your WATI token in WATI Dashboard → Settings → API. The base URL is the live server URL shown in your WATI account.
                </div>
              )}
              {draft.provider === 'twilio' && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef3c7', borderRadius: 8, fontSize: 11, color: '#92400e' }}>
                  💡 Find Account SID & Auth Token in Twilio Console dashboard. The WhatsApp number is from the Twilio WhatsApp sandbox (or approved sender).
                </div>
              )}
              {draft.provider === 'aisensy' && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, fontSize: 11, color: '#166534' }}>
                  💡 Find your API Key in AiSensy Dashboard → Settings → API. The Campaign Name is the template name you created for sending messages (must be approved). User Name is your AiSensy account display name.
                </div>
              )}
              {draft.provider === 'meta' && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, fontSize: 11, color: '#1e40af' }}>
                  💡 Find Phone Number ID & Access Token in Meta Developer Portal → Your App → WhatsApp → API Setup. Use "hello_world" template to test (pre-approved). For custom messages, create & get templates approved in WhatsApp Manager.
                </div>
              )}
            </div>
          )}

          {/* Test section */}
          <div style={{ padding: '14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10 }}>🧪 Test Connection</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="tel"
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
                placeholder="Test phone number (e.g. 9876543210)"
                style={{ flex: 1, padding: '7px 10px', borderRadius: 7, border: '1.5px solid #d1d5db', fontSize: 12, outline: 'none' }}
              />
              <button
                onClick={handleTest}
                disabled={testing}
                style={{ padding: '7px 16px', background: accentColor, color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer', flexShrink: 0 }}
              >
                {testing ? '⏳ Sending…' : '📤 Send Test'}
              </button>
            </div>
            {testResult && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, background: testResult.ok ? '#dcfce7' : '#fee2e2', color: testResult.ok ? '#15803d' : '#dc2626' }}>
                {testResult.msg}
              </div>
            )}
          </div>

          {/* Current status */}
          <div style={{ padding: '10px 14px', background: '#f0fdfa', borderRadius: 8, border: '1px solid #6ee7b7', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>📲</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0f766e' }}>Currently Active: {provider.label}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>All WhatsApp messages across the entire app use this provider</div>
            </div>
          </div>

          {/* Footer buttons */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setConfigOpen(false)}
              style={{ padding: '9px 18px', background: '#fff', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ padding: '9px 20px', background: accentColor, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
            >
              {saving ? 'Saving…' : '💾 Save Configuration'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
